import type { deriveSeoReport } from "@aix/aixinternal-shared/reportingView"

const number = (value: number | null) => (value === null ? "Unknown" : value.toLocaleString("en-US"))

export function PageGroupReport({ report }: { report: ReturnType<typeof deriveSeoReport>[number] }) {
  const data = report.pageGroups
  const max = Math.max(1, ...data.groups.map((group) => group.clicks))
  return (
    <section className="space-y-3" aria-label={`${report.product.name} page contributions`}>
      <h3 className="font-medium">Where page clicks came from</h3>
      <p className="text-sm text-muted-foreground">
        {report.currentDates[0]}–{report.currentDates.at(-1)} · {data.exportDays}/{data.expectedDays} complete
        page-export days{data.exportComplete ? "" : " · partial observations only"}. Page-export totals, not property
        totals.
      </p>
      {data.groups.length ? (
        <figure>
          <figcaption className="mb-2 text-sm">Page clicks · common scale 0–{number(max)}</figcaption>
          {data.groups.map((group) => (
            <div key={group.key} className="grid grid-cols-[9rem_minmax(0,1fr)_3rem] items-center gap-2 py-1 text-sm">
              <span>{group.label}</span>
              <svg aria-hidden="true" viewBox="0 0 300 18" preserveAspectRatio="none" className="h-[18px] w-full">
                <rect x="0" y="0" width={(group.clicks / max) * 300} height="18" className="fill-primary" />
              </svg>
              <span className="text-right tabular-nums">{number(group.clicks)}</span>
            </div>
          ))}
        </figure>
      ) : (
        <p className="text-sm">
          Contribution unavailable: {data.reason ?? "NO_PAGE_OBSERVATIONS"}. No zero-filled group chart.
        </p>
      )}
      <details className="rounded-md border border-border p-3">
        <summary className="cursor-pointer text-sm font-medium">Catalog coverage and page-group evidence</summary>
        <div className="mt-3 space-y-3 text-sm">
          <p>
            Catalog at {data.collectedAt ?? "unknown"}; binding {data.sourceKey ?? "not configured"}. Current catalog
            classifications are applied to these source dates, not reconstructed historical page states.
          </p>
          <p>
            {number(data.managedPages)} managed leaves · {number(data.publishedPages)} marked published in AIX ·{" "}
            {number(data.observedPublished)} published leaves with returned GSC rows ·{" "}
            {number(data.publishedWithoutReturnedRow)} without a returned row in a complete export. This does not prove
            deployment or Google indexation. Missing exports leave the absent-page count unknown.
          </p>
          <p>
            Exact canonical URLs only. Tools use explicit stored paths. Query, slash, protocol and hostname variants
            stay separate; {number(data.variantUrls)} potential variants are included in Other, not merged.
          </p>
          {data.groups.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr>
                    <th className="p-2">Group</th>
                    <th className="p-2">Clicks</th>
                    <th className="p-2">Impressions</th>
                    <th className="p-2">Returned URLs</th>
                    <th className="p-2">Click share</th>
                  </tr>
                </thead>
                <tbody>
                  {data.groups.map((group) => (
                    <tr key={group.key} className="border-t border-border">
                      <td className="p-2">{group.label}</td>
                      <td className="p-2">{number(group.clicks)}</td>
                      <td className="p-2">{number(group.impressions)}</td>
                      <td className="p-2">{group.urls}</td>
                      <td className="p-2">
                        {group.share === null ? "No click baseline" : `${(group.share * 100).toFixed(1)}%`}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t border-border">
                    <th className="p-2">Same page dataset total</th>
                    <td className="p-2">{number(data.totalClicks)}</td>
                    <td className="p-2">{number(data.totalImpressions)}</td>
                    <td className="p-2" colSpan={2}>
                      Each returned URL counted once
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : null}
          <p className="break-all text-xs text-muted-foreground">
            Stored collection IDs: {data.collectionIds.join(", ") || "none"}
          </p>
        </div>
      </details>
    </section>
  )
}
